import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  CalendarDays, Check, CheckCircle2, Copy, Hotel,
  Loader2, Printer, ShieldCheck, Users,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { HOTEL, inr } from "@/constants/hotelData";
import { Logo } from "@/components/Logo";

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};
const fmtDate = (s) =>
  new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const STEPS = ["Stay", "Guests", "Payment", "Done"];

const METHODS = [
  { id: "online", label: "Pay Online", desc: "UPI, Cards, Netbanking, Wallets", icon: ShieldCheck },
  { id: "pay_at_hotel", label: "Pay at Hotel", desc: "Cash / card on check-in", icon: Hotel },
];

const RETURN_MESSAGES = {
  failed: "Payment failed or was declined. You can try again or choose Pay at Hotel.",
  cancelled: "Payment was cancelled. You can try again or choose Pay at Hotel.",
  error: "We could not verify the payment. Please try again or contact the hotel.",
};

const submitToGateway = ({ gateway_url, access_code, encRequest }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = gateway_url;
  Object.entries({ encRequest, access_code }).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
};

export default function BookingPage() {
  const [params, setParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [checkIn, setCheckIn] = useState(params.get("checkin") || todayStr());
  const [checkOut, setCheckOut] = useState(params.get("checkout") || addDays(todayStr(), 1));
  const [rooms, setRooms] = useState(parseInt(params.get("rooms") || "1", 10) || 1);
  const [guests, setGuests] = useState(2);
  const [avail, setAvail] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [booking, setBooking] = useState(null);
  const [method, setMethod] = useState("online");
  const [paying, setPaying] = useState(false);
  const [restoring, setRestoring] = useState(Boolean(params.get("ref")));

  const nights = useMemo(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return 0;
    return Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
  }, [checkIn, checkOut]);
  const total = nights * HOTEL.ratePerNight * rooms;

  // Returning from the CCAvenue gateway: restore the booking and show the right step.
  useEffect(() => {
    const ref = params.get("ref");
    const outcome = params.get("payment");
    if (!ref) {
      if (outcome) {
        toast.error(RETURN_MESSAGES[outcome] || RETURN_MESSAGES.error);
        setParams({}, { replace: true });
      }
      return;
    }
    let live = true;
    api
      .get(`/bookings/${ref}`)
      .then(({ data }) => {
        if (!live) return;
        setBooking(data);
        setCheckIn(data.check_in);
        setCheckOut(data.check_out);
        setRooms(data.rooms);
        setGuests(data.guests);
        if (data.payment_status === "paid" || data.payment_status === "pay_at_hotel") {
          setStep(4);
          if (outcome === "success") toast.success("Payment successful — booking confirmed!");
        } else {
          setStep(3);
          toast.error(RETURN_MESSAGES[outcome] || RETURN_MESSAGES.error);
        }
      })
      .catch(() => live && toast.error("Booking not found"))
      .finally(() => {
        if (live) {
          setRestoring(false);
          setParams({}, { replace: true });
        }
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!checkIn || !checkOut || checkOut <= checkIn) return;
    let live = true;
    api
      .get("/availability", { params: { check_in: checkIn, check_out: checkOut } })
      .then(({ data }) => live && setAvail(data))
      .catch(() => live && setAvail(null));
    return () => {
      live = false;
    };
  }, [checkIn, checkOut]);

  useEffect(() => {
    if (step >= 3 && !booking) setStep(1);
  }, [step, booking]);

  const stayValid = nights >= 1 && avail && avail.available >= rooms;
  const phoneDigits = form.phone.replace(/\D/g, "");
  const guestValid =
    form.name.trim().length >= 2 && phoneDigits.length >= 10 && phoneDigits.length <= 13;

  const submitGuests = async () => {
    try {
      const { data } = await api.post("/bookings", {
        guest_name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        check_in: checkIn,
        check_out: checkOut,
        rooms,
        guests,
        notes: form.notes.trim() || undefined,
      });
      setBooking(data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmPayment = async () => {
    setPaying(true);
    try {
      if (method === "pay_at_hotel") {
        const { data } = await api.post(`/bookings/${booking.ref}/pay`, { method });
        setBooking(data);
        setStep(4);
        toast.success("Booking confirmed — pay at check-in");
        setPaying(false);
        return;
      }
      const { data } = await api.post("/payments/ccavenue/initiate", { ref: booking.ref });
      submitToGateway(data); // full-page redirect to CCAvenue; we return via /book?ref=…&payment=…
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  const copyRef = () => {
    navigator.clipboard?.writeText(booking.ref);
    toast.success("Booking ID copied");
  };

  const stepCircle = (n) => {
    const done = step > n;
    const active = step === n;
    return (
      <div key={n} className="flex items-center">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-all duration-300 ${
            done
              ? "border-gold bg-gold text-ink"
              : active
                ? "border-gold text-gold shadow-[0_0_16px_rgba(212,175,55,0.35)]"
                : "border-cream/25 text-cream/50"
          }`}
        >
          {done ? <Check className="h-4 w-4" /> : n}
        </div>
        <span className={`ml-2 hidden text-[10px] uppercase tracking-[0.2em] sm:block ${active ? "text-gold" : "text-cream/50"}`}>
          {STEPS[n - 1]}
        </span>
        {n < 4 && <div className={`mx-3 h-px w-5 sm:w-10 ${step > n ? "bg-gold" : "bg-cream/20"}`} />}
      </div>
    );
  };

  const inputCls =
    "w-full rounded-lg border border-cream/15 bg-ink/40 px-3.5 py-2.5 text-sm text-cream outline-none transition-colors placeholder:text-cream/35 focus:border-gold/60";

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="glass sticky top-0 z-40 py-3">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="booking-logo-link">
            <Logo />
          </Link>
          <div className="hidden font-mono text-xs text-goldlight/90 sm:block">
            {inr(HOTEL.ratePerNight)}/night · {HOTEL.totalRooms} rooms
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <h1 className="font-serif text-3xl tracking-tight sm:text-4xl">
          Book your <span className="gold-gradient-text italic">royal stay</span>
        </h1>
        <div className="mt-8 flex items-center" data-testid="booking-steps">
          {[1, 2, 3, 4].map(stepCircle)}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {restoring && (
              <div className="flex items-center gap-3 rounded-2xl border border-gold/15 p-8 text-sm text-stone-300" data-testid="payment-restoring">
                <Loader2 className="h-4 w-4 animate-spin text-gold" /> Verifying your payment…
              </div>
            )}
            <AnimatePresence mode="wait">
              {step === 1 && !restoring && (
                <motion.div
                  key="s1"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                  className="shimmer-border rounded-2xl p-6 sm:p-8"
                >
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <CalendarDays className="h-5 w-5 text-gold" /> Stay details
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Check In</label>
                      <input type="date" data-testid="checkin-date-input" value={checkIn} min={todayStr()} onChange={(e) => { setCheckIn(e.target.value); if (e.target.value && checkOut <= e.target.value) setCheckOut(addDays(e.target.value, 1)); }} className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Check Out</label>
                      <input type="date" data-testid="checkout-date-input" value={checkOut} min={addDays(checkIn, 1)} onChange={(e) => setCheckOut(e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Rooms</label>
                      <select data-testid="rooms-count-select" value={rooms} onChange={(e) => setRooms(Number(e.target.value))} className={inputCls}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n} className="bg-ink">{n} room{n > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Guests</label>
                      <select data-testid="guests-count-select" value={guests} onChange={(e) => setGuests(Number(e.target.value))} className={inputCls}>
                        {Array.from({ length: rooms * 4 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n} className="bg-ink">{n} guest{n > 1 ? "s" : ""}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-6 rounded-xl border border-gold/20 bg-ink/40 p-4" data-testid="availability-badge">
                    {avail ? (
                      <p className="text-sm text-stone-300">
                        <span className="font-semibold text-gold">{avail.available} of {avail.total_rooms} rooms</span> available for these dates
                      </p>
                    ) : (
                      <p className="text-sm text-stone-400">Select valid dates to see availability…</p>
                    )}
                  </div>

                  <button
                    data-testid="step1-continue-button"
                    disabled={!stayValid}
                    onClick={() => setStep(2)}
                    className="mt-7 w-full rounded-full bg-gold py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 enabled:hover:bg-goldlight enabled:hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Continue to Guest Details
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="s2"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                  className="shimmer-border rounded-2xl p-6 sm:p-8"
                >
                  <h2 className="flex items-center gap-2 font-serif text-2xl">
                    <Users className="h-5 w-5 text-gold" /> Guest details
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Full Name *</label>
                      <input data-testid="guest-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Rahul Kumar" className={inputCls} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Phone *</label>
                      <input data-testid="guest-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile number" inputMode="tel" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Email (optional · for payment receipt)</label>
                      <input data-testid="guest-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com — we'll email your receipt here" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-[10px] uppercase tracking-[0.24em] text-goldlight/80">Special Requests (optional)</label>
                      <textarea data-testid="guest-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Early check-in, extra pillow, celebration…" className={inputCls} />
                    </div>
                  </div>
                  <div className="mt-7 flex gap-4">
                    <button data-testid="step2-back-button" onClick={() => setStep(1)} className="rounded-full border border-cream/25 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/80 transition-colors hover:border-gold hover:text-gold">
                      Back
                    </button>
                    <button
                      data-testid="step2-continue-button"
                      disabled={!guestValid}
                      onClick={submitGuests}
                      className="flex-1 rounded-full bg-gold py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 enabled:hover:bg-goldlight enabled:hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && booking && (
                <motion.div
                  key="s3"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35 }}
                  className="shimmer-border rounded-2xl p-6 sm:p-8"
                >
                  <h2 className="font-serif text-2xl">Payment</h2>
                  <p className="mt-1 text-xs font-light text-stone-400">
                    Secure payment powered by CCAvenue · Booking ID <span className="font-mono text-gold">{booking.ref}</span>
                  </p>

                  <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {METHODS.map((m) => (
                      <button
                        key={m.id}
                        data-testid={`payment-method-${m.id}`}
                        onClick={() => setMethod(m.id)}
                        className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                          method === m.id
                            ? "border-gold bg-gold/10 shadow-[0_0_20px_rgba(212,175,55,0.2)]"
                            : "border-cream/15 bg-ink/40 hover:border-gold/40"
                        }`}
                      >
                        <m.icon className={`h-5 w-5 ${method === m.id ? "text-gold" : "text-cream/60"}`} />
                        <div className={`mt-2 text-sm font-medium ${method === m.id ? "text-cream" : "text-cream/80"}`}>{m.label}</div>
                        <div className="mt-0.5 text-[11px] font-light text-stone-400">{m.desc}</div>
                      </button>
                    ))}
                  </div>

                  {method === "online" && (
                    <div className="mt-6 rounded-xl border border-gold/20 bg-ink/40 p-6 text-sm font-light text-stone-300" data-testid="online-payment-panel">
                      <p>
                        You will be securely redirected to the <span className="text-cream">CCAvenue</span> payment page to pay{" "}
                        <span className="font-semibold text-gold">{inr(total)}</span> using UPI (GPay, PhonePe, Paytm), debit/credit card, netbanking or wallets.
                      </p>
                      <p className="mt-3 flex items-center gap-2 text-[11px] text-stone-500">
                        <ShieldCheck className="h-3.5 w-3.5 text-gold/70" /> 256-bit encrypted · PCI-DSS compliant gateway · You return here automatically after payment
                      </p>
                    </div>
                  )}

                  {method === "pay_at_hotel" && (
                    <div className="mt-6 rounded-xl border border-gold/20 bg-ink/40 p-6 text-sm font-light text-stone-300" data-testid="pay-at-hotel-panel">
                      Your room will be reserved now. Pay {inr(total)} in cash or by card when you arrive at the front desk.
                    </div>
                  )}

                  <div className="mt-7 flex gap-4">
                    <button data-testid="step3-back-button" onClick={() => setStep(2)} disabled={paying} className="rounded-full border border-cream/25 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/80 transition-colors hover:border-gold hover:text-gold disabled:opacity-40">
                      Back
                    </button>
                    <button
                      data-testid="pay-button"
                      disabled={paying}
                      onClick={confirmPayment}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all duration-300 enabled:hover:bg-goldlight enabled:hover:shadow-[0_0_28px_rgba(212,175,55,0.4)] disabled:opacity-60"
                    >
                      {paying ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> {method === "online" ? "Redirecting to CCAvenue…" : "Confirming…"}
                        </>
                      ) : method === "pay_at_hotel" ? (
                        "Confirm Booking"
                      ) : (
                        `Pay ${inr(total)} Securely`
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 4 && booking && (
                <motion.div
                  key="s4"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="shimmer-border rounded-2xl p-8 text-center sm:p-12"
                  data-testid="booking-confirmation"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.15 }}
                    className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gold/15"
                  >
                    <CheckCircle2 className="h-10 w-10 text-gold" />
                  </motion.div>
                  <h2 className="mt-6 font-serif text-3xl text-cream sm:text-4xl">
                    {booking.payment_status === "paid" ? "Booking Confirmed" : "Room Reserved"}
                  </h2>
                  <p className="mt-2 text-sm font-light text-stone-400">
                    {booking.payment_status === "paid"
                      ? booking.email
                        ? `Payment received. A receipt has been emailed to ${booking.email}.`
                        : "Payment received. Your royal stay is locked in."
                      : `Please pay ${inr(booking.amount)} at the front desk on arrival.`}
                  </p>

                  <button
                    data-testid="copy-booking-id-button"
                    onClick={copyRef}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-2.5 font-mono text-sm text-gold transition-colors hover:bg-gold/10"
                  >
                    {booking.ref} <Copy className="h-3.5 w-3.5" />
                  </button>

                  <div className="mx-auto mt-8 grid max-w-md grid-cols-2 gap-4 text-left text-sm">
                    <div className="text-stone-400">Guest</div>
                    <div className="text-cream" data-testid="confirmation-guest">{booking.guest_name}</div>
                    <div className="text-stone-400">Stay</div>
                    <div className="text-cream" data-testid="confirmation-dates">{fmtDate(booking.check_in)} → {fmtDate(booking.check_out)}</div>
                    <div className="text-stone-400">Rooms · Guests</div>
                    <div className="text-cream">{booking.rooms} room{booking.rooms > 1 ? "s" : ""} · {booking.guests} guest{booking.guests > 1 ? "s" : ""}</div>
                    <div className="text-stone-400">Amount</div>
                    <div className="font-semibold text-gold" data-testid="confirmation-amount">{inr(booking.amount)}</div>
                  </div>

                  <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                    <button data-testid="print-voucher-button" onClick={() => window.print()} className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/25 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-cream/80 transition-colors hover:border-gold hover:text-gold">
                      <Printer className="h-4 w-4" /> Print Voucher
                    </button>
                    <Link to="/" data-testid="back-home-button" className="inline-flex items-center justify-center rounded-full bg-gold px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-ink transition-all hover:bg-goldlight">
                      Back to Home
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-gold/15 bg-[#0f241b] p-6" data-testid="booking-summary">
              <h3 className="text-[10px] font-medium uppercase tracking-[0.28em] text-gold">Booking Summary</h3>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-stone-400">Check-in</span><span className="text-cream">{checkIn ? fmtDate(checkIn) : "—"}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">Check-out</span><span className="text-cream">{checkOut ? fmtDate(checkOut) : "—"}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">Nights</span><span className="text-cream">{nights || "—"}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">Rooms</span><span className="text-cream">{rooms}</span></div>
                <div className="flex justify-between"><span className="text-stone-400">Rate</span><span className="text-cream">{inr(HOTEL.ratePerNight)} / night</span></div>
              </div>
              <div className="mt-5 flex items-center justify-between border-t border-gold/15 pt-5">
                <span className="text-xs uppercase tracking-[0.2em] text-cream/70">Total</span>
                <span className="font-serif text-2xl text-gold" data-testid="summary-total">{total ? inr(total) : "—"}</span>
              </div>
              <p className="mt-4 text-[11px] font-light leading-relaxed text-stone-500">
                Free cancellation up to 24 hours before check-in. Bookings are
                recorded in the hotel register instantly.
              </p>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
